import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CALENDAR_TYPES, MONTHS } from './CalendarView';

export default function SeedFormModal({ isOpen, onClose, onSave, seed }) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('🌱');
  const [distanceCm, setDistanceCm] = useState(10);
  const [varieties, setVarieties] = useState([]);
  
  const [calendar, setCalendar] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (seed) {
        setName(seed.name);
        setIcon(seed.icon);
        setDistanceCm(seed.distanceCm);
        setVarieties(seed.varieties ? [...seed.varieties] : []);
        setCalendar(seed.calendar ? JSON.parse(JSON.stringify(seed.calendar)) : getEmptyCalendar());
      } else {
        setName('');
        setIcon('🌱');
        setDistanceCm(10);
        setVarieties([]);
        setCalendar(getEmptyCalendar());
      }
    }
  }, [isOpen, seed]);

  const getEmptyCalendar = () => {
    const empty = {};
    for (let i = 0; i < 12; i++) empty[i] = [];
    return empty;
  };

  if (!isOpen) return null;

  const handleSave = (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    onSave({
      id: seed ? seed.id : name.toLowerCase().replace(/[^a-z0-9]/g, '_') + '_' + Date.now(),
      name: name.trim(),
      icon: icon.trim() || '🌱',
      distanceCm: Math.max(1, distanceCm),
      distanceStr: `${Math.max(1, distanceCm)} cm`,
      varieties: varieties,
      calendar: calendar
    });
  };

  const toggleMonthType = (monthIndex, typeId) => {
    setCalendar(prev => {
      const newCal = { ...prev };
      const monthTypes = newCal[monthIndex] || [];
      if (monthTypes.includes(typeId)) {
        newCal[monthIndex] = monthTypes.filter(t => t !== typeId);
      } else {
        newCal[monthIndex] = [...monthTypes, typeId];
      }
      return newCal;
    });
  };

  return createPortal(
    <div className="modal-overlay" style={{ overflowY: 'auto' }} onClick={(e) => {
      if(e.target.className === 'modal-overlay') onClose();
    }}>
      <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>
            {seed ? 'Editar Semilla' : 'Nueva Semilla'}
          </h3>
          <button className="close-btn" onClick={onClose} type="button">&times;</button>
        </div>

        <form onSubmit={handleSave}>
          <div className="input-group">
            <label htmlFor="seedName">Nombre</label>
            <input 
              type="text" 
              id="seedName" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Sandía Gigante"
              required
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div className="input-group" style={{ flex: 1 }}>
              <label htmlFor="seedIcon">Icono (Emoji)</label>
              <input 
                type="text" 
                id="seedIcon" 
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="🍉"
                maxLength={4}
              />
            </div>

            <div className="input-group" style={{ flex: 1 }}>
              <label htmlFor="seedDistance">Distancia Mín. (cm)</label>
              <input 
                type="number" 
                id="seedDistance" 
                min="1"
                max="10000"
                value={distanceCm}
                onChange={(e) => setDistanceCm(parseInt(e.target.value) || 1)}
                required
              />
            </div>
          </div>

          <div className="input-group">
            <label>Variedades (opcional)</label>
            {varieties.length > 0 ? (
              <ul style={{ padding: 0, listStyle: 'none', margin: '0 0 12px 0' }}>
                {varieties.map((v, index) => (
                  <li key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.05)', padding: '6px 10px', marginBottom: '4px', borderRadius: '4px' }}>
                    <span>{v}</span>
                    <button 
                      type="button" 
                      onClick={() => setVarieties(varieties.filter((_, i) => i !== index))} 
                      style={{ background: 'none', border: 'none', color: 'var(--danger-color)', cursor: 'pointer' }}
                    >
                      ✖
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <input 
                type="text" 
                id="newVariety"
                placeholder="Nueva variedad..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = e.target.value.trim();
                    if (val && !varieties.includes(val)) {
                      setVarieties([...varieties, val]);
                      e.target.value = '';
                    }
                  }
                }}
              />
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  const input = document.getElementById('newVariety');
                  const val = input.value.trim();
                  if (val && !varieties.includes(val)) {
                    setVarieties([...varieties, val]);
                    input.value = '';
                  }
                }}
              >
                Añadir
              </button>
            </div>
          </div>

          <div className="input-group mt-4">
            <label>Calendario de Siembra</label>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px', fontSize: '10px' }}>
              {CALENDAR_TYPES.map(type => (
                <div key={type.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: type.color, display: 'inline-block' }}></span>
                  {type.name}
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {MONTHS.map((month, mIndex) => (
                <div key={month} style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '6px', borderRadius: '4px', border: '1px solid #333' }}>
                  <div style={{ fontSize: '10px', textAlign: 'center', marginBottom: '6px', fontWeight: 'bold' }}>{month}</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                    {CALENDAR_TYPES.map(type => {
                      const isActive = calendar[mIndex]?.includes(type.id);
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => toggleMonthType(mIndex, type.id)}
                          style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            border: isActive ? `2px solid white` : '1px solid #666',
                            backgroundColor: isActive ? type.color : 'rgba(255,255,255,0.05)',
                            boxShadow: isActive ? `0 0 6px ${type.color}` : 'none',
                            padding: 0,
                            cursor: 'pointer',
                            opacity: isActive ? 1 : 0.5,
                            transition: 'all 0.2s ease-in-out'
                          }}
                          title={type.name}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button type="submit" className="btn mt-4">
            💾 Guardar Semilla
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}
